import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Cell, CellWithEnrollToken, CustomerSpec, CustomerSpecWithInvite, Release, RolloutResult, SpecChannel } from "@/types";

// #343 — Schreiber der Provisionierung. Jede Mutation invalidiert die betroffenen Ressourcen (Spezifikationen
// haengen an Zellen UND Releases: Rollout aendert Spezifikationen).
function useClient() {
  const { session, logout } = useAuth();
  const qc = useQueryClient();
  return {
    client: () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout }),
    invalidate: (...types: string[]) => Promise.all(types.map((t) => qc.invalidateQueries({ queryKey: queryKeys.resource(t) }))),
  };
}

export function useCreateCell(): UseMutationResult<CellWithEnrollToken, Error, { name: string; provider: string }> {
  const { client, invalidate } = useClient();
  return useMutation({ mutationFn: (input) => client().post<CellWithEnrollToken>("/cells", input), onSuccess: () => invalidate("cells") });
}

export function useIssueEnrollToken(): UseMutationResult<CellWithEnrollToken, Error, string> {
  const { client, invalidate } = useClient();
  return useMutation({ mutationFn: (id) => client().post<CellWithEnrollToken>(`/cells/${id}/enroll-token`, {}), onSuccess: () => invalidate("cells") });
}

export interface CreateSpecInput {
  customerId: string;
  cellId: string;
  customer: string;
  plan: string;
  imageTag: string;
  host?: string | null;
  features?: { relay?: boolean; litellm?: boolean };
  values?: Record<string, unknown>;
  updates?: { mode?: string; channel?: string; window?: string | null };
  adminEmail?: string | null;
  // Kanaele (#341): Name, Typ, Anzeigename, nicht-geheime ENV — das Bundle entsteht in der Zelle, Tokens/QR beim Kunden.
  channels?: SpecChannel[];
  licenseId?: string | null;
  registry?: { username: string; token: string } | null;
}

// #343 (UC-9): die Antwort trägt die Einladung des Erst-Admins (Klartext genau einmal) — der Aufrufer zeigt sie sofort.
export function useCreateSpec(): UseMutationResult<CustomerSpecWithInvite, Error, CreateSpecInput> {
  const { client, invalidate } = useClient();
  return useMutation({ mutationFn: (input) => client().post<CustomerSpecWithInvite>("/customer-specs", input), onSuccess: () => invalidate("customer-specs", "provision-audit") });
}

// #343 (UC-9): Einladung erneuern / Admin-Zugang zurücksetzen (neues Token, neue Auftrags-Version).
export function useRenewAdminInvite(): UseMutationResult<CustomerSpecWithInvite, Error, string> {
  const { client, invalidate } = useClient();
  return useMutation({ mutationFn: (id) => client().post<CustomerSpecWithInvite>(`/customer-specs/${id}/admin-invite`, {}), onSuccess: () => invalidate("customer-specs", "provision-audit") });
}

export interface UpdateSpecInput {
  id: string;
  patch: Partial<Pick<CreateSpecInput, "plan" | "imageTag" | "host" | "features" | "values" | "updates" | "adminEmail" | "channels">> & { chartVersion?: string | null; chartChecksum?: string | null };
}

export function useUpdateSpec(): UseMutationResult<CustomerSpec, Error, UpdateSpecInput> {
  const { client, invalidate } = useClient();
  return useMutation({ mutationFn: ({ id, patch }) => client().put<CustomerSpec>(`/customer-specs/${id}`, patch), onSuccess: () => invalidate("customer-specs", "provision-audit") });
}

export function useRemoveSpec(): UseMutationResult<CustomerSpec, Error, string> {
  const { client, invalidate } = useClient();
  return useMutation({ mutationFn: (id) => client().post<CustomerSpec>(`/customer-specs/${id}/remove`, {}), onSuccess: () => invalidate("customer-specs", "provision-audit") });
}

export function useSetSpecRegistry(): UseMutationResult<CustomerSpec, Error, { id: string; registry: { username: string; token: string } | null }> {
  const { client, invalidate } = useClient();
  return useMutation({ mutationFn: ({ id, registry }) => client().put<CustomerSpec>(`/customer-specs/${id}/registry`, { registry }), onSuccess: () => invalidate("customer-specs", "provision-audit") });
}

export function useReissueActivationKey(): UseMutationResult<CustomerSpec, Error, { id: string; licenseId?: string }> {
  const { client, invalidate } = useClient();
  return useMutation({
    mutationFn: ({ id, licenseId }) => client().post<CustomerSpec>(`/customer-specs/${id}/activation-key`, licenseId ? { licenseId } : {}),
    onSuccess: () => invalidate("customer-specs", "provision-audit", "licenses"),
  });
}

export function useRegisterRelease(): UseMutationResult<Release, Error, { version: string; notes?: string }> {
  const { client, invalidate } = useClient();
  return useMutation({ mutationFn: (input) => client().post<Release>("/releases", input), onSuccess: () => invalidate("releases") });
}

export function usePromoteRelease(): UseMutationResult<Release, Error, string> {
  const { client, invalidate } = useClient();
  return useMutation({ mutationFn: (version) => client().post<Release>(`/releases/${encodeURIComponent(version)}/promote`, {}), onSuccess: () => invalidate("releases") });
}

export function useRollout(): UseMutationResult<{ results: RolloutResult[] }, Error, { version: string; cellId?: string; specIds?: string[] }> {
  const { client, invalidate } = useClient();
  return useMutation({
    mutationFn: ({ version, ...rest }) => client().post<{ results: RolloutResult[] }>(`/releases/${encodeURIComponent(version)}/rollout`, rest),
    onSuccess: () => invalidate("customer-specs", "provision-audit"),
  });
}

export type { Cell };
